import heic2any from 'heic2any';

/**
 * Check whether a file is HEIC/HEIF format (by extension or MIME type).
 */
function isHeicFile(file: File): boolean {
  const name = file.name.toLowerCase();
  if (name.endsWith('.heic') || name.endsWith('.heif')) return true;
  const type = file.type.toLowerCase();
  return type === 'image/heic' || type === 'image/heif';
}

/**
 * Detect if we're running on iPhone Safari for optimizations
 */
function isiPhoneSafari(): boolean {
  const ua = navigator.userAgent;
  return /iPhone/i.test(ua) && /Safari/i.test(ua) && !/Chrome/i.test(ua);
}

/**
 * Race a promise against a timeout. Rejects with an Error if the timeout fires first.
 */
function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms);
    promise.then(
      (v) => { clearTimeout(timer); resolve(v); },
      (e) => { clearTimeout(timer); reject(e); },
    );
  });
}

/** Max time (ms) to spend on client-side HEIC→JPEG conversion before giving up. 
 * Reduced for iPhone Safari to fail fast and let backend handle HEIC. */
const HEIC_TIMEOUT_MS = isiPhoneSafari() ? 5_000 : 10_000;

/**
 * Convert a HEIC/HEIF file to a JPEG File using heic2any.
 * This must happen BEFORE any canvas/OffscreenCanvas processing because
 * most browsers cannot decode HEIC natively via createImageBitmap.
 *
 * PRODUCTION OPTIMIZED for iPhone Safari:
 * - Faster timeout to avoid hanging UI
 * - Lower quality for faster conversion
 * - Better error handling for mobile Safari quirks
 *
 * Returns null if conversion fails or times out - the caller should
 * fall back to uploading the raw HEIC (the backend handles it via pillow-heif).
 */
async function convertHeicToJpeg(file: File): Promise<File | null> {
  try {
    console.log(`🔄 Converting HEIC to JPEG: ${file.name} (${(file.size/1024/1024).toFixed(1)}MB)`);
    
    // iPhone Safari optimization: Use lower quality for faster conversion
    const quality = isiPhoneSafari() ? 0.85 : 0.92;
    
    const blob = await withTimeout(
      heic2any({ blob: file, toType: 'image/jpeg', quality }),
      HEIC_TIMEOUT_MS,
      'HEIC conversion',
    );

    const resultBlob = Array.isArray(blob) ? blob[0] : blob;
    const name = file.name.replace(/\.[^.]+$/, '.jpg');
    const convertedFile = new File([resultBlob], name, {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
    
    console.log(`✅ HEIC converted: ${(convertedFile.size/1024/1024).toFixed(1)}MB JPEG`);
    return convertedFile;
  } catch (err) {
    console.warn('📤 Client-side HEIC conversion failed, sending raw file to backend:', err);
    return null;
  }
}

/**
 * Compress an image file using canvas.
 * PRODUCTION OPTIMIZED for iPhone Safari uploads:
 * - Smaller max dimensions for mobile (faster processing)
 * - Better quality/size balance for web upload
 * - Optimized canvas handling for Safari
 * - Fail-fast error handling
 *
 * HEIC/HEIF images are first converted to JPEG via heic2any so that
 * createImageBitmap / OffscreenCanvas can decode them.
 */
export async function compressImage(
  file: File,
  maxDimension?: number,
  quality?: number,
): Promise<File> {
  console.log(`📷 Compressing: ${file.name} (${(file.size/1024/1024).toFixed(1)}MB)`);
  
  // iPhone Safari optimizations: smaller dimensions for faster processing
  const defaultMaxDimension = isiPhoneSafari() ? 1440 : 1920; // 1440p vs 1080p for mobile
  const defaultQuality = isiPhoneSafari() ? 0.75 : 0.8; // Slightly lower quality for speed
  
  maxDimension = maxDimension || defaultMaxDimension;
  quality = quality || defaultQuality;

  // Convert HEIC/HEIF to JPEG first - browsers can't decode HEIC on canvas.
  // If conversion fails/times out, return the raw HEIC so the backend can handle it.
  let input = file;
  if (isHeicFile(file)) {
    const converted = await convertHeicToJpeg(file);
    if (!converted) {
      console.log('📤 Sending raw HEIC to backend for server-side processing');
      return file; // Send raw HEIC to backend (it has pillow-heif)
    }
    input = converted;
  }

  // iPhone Safari: More aggressive size threshold to reduce processing time
  const sizeThreshold = isiPhoneSafari() ? 800 * 1024 : 1024 * 1024; // 800KB vs 1MB
  if (input.size <= sizeThreshold) {
    console.log(`⚡ File under ${(sizeThreshold/1024).toFixed(0)}KB, skipping compression`);
    return input;
  }

  try {
    const bitmap = await createImageBitmap(input);
    const { width, height } = bitmap;

    // Calculate scaled dimensions
    let newWidth = width;
    let newHeight = height;
    if (width > maxDimension || height > maxDimension) {
      const ratio = Math.min(maxDimension / width, maxDimension / height);
      newWidth = Math.round(width * ratio);
      newHeight = Math.round(height * ratio);
    }

    console.log(`📐 Resizing: ${width}x${height} → ${newWidth}x${newHeight}`);

    // Use regular Canvas on iPhone Safari (OffscreenCanvas can be buggy)
    let canvas: OffscreenCanvas | HTMLCanvasElement;
    let ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;
    
    if (isiPhoneSafari() && typeof document !== 'undefined') {
      // Use regular canvas for iPhone Safari compatibility
      canvas = document.createElement('canvas');
      canvas.width = newWidth;
      canvas.height = newHeight;
      ctx = canvas.getContext('2d')!;
    } else {
      // Use OffscreenCanvas for better performance on other browsers
      canvas = new OffscreenCanvas(newWidth, newHeight);
      ctx = canvas.getContext('2d')!;
    }

    ctx.drawImage(bitmap, 0, 0, newWidth, newHeight);
    bitmap.close();

    // Export as JPEG blob
    let blob: Blob;
    if (canvas instanceof HTMLCanvasElement) {
      // Convert HTMLCanvasElement to blob
      blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((b) => {
          if (b) resolve(b);
          else reject(new Error('Canvas toBlob failed'));
        }, 'image/jpeg', quality);
      });
    } else {
      // OffscreenCanvas convertToBlob
      blob = await canvas.convertToBlob({ type: 'image/jpeg', quality });
    }

    // Return as File with original name (but .jpg extension)
    const name = file.name.replace(/\.[^.]+$/, '.jpg');
    const compressedFile = new File([blob], name, { 
      type: 'image/jpeg', 
      lastModified: Date.now() 
    });
    
    const compressionRatio = ((file.size - compressedFile.size) / file.size * 100).toFixed(1);
    console.log(`✅ Compressed: ${(compressedFile.size/1024/1024).toFixed(1)}MB (-${compressionRatio}%)`);
    
    return compressedFile;
  } catch (error) {
    console.warn('❌ Compression failed, returning original:', error);
    return input; // Return original if compression fails
  }
}

/**
 * PRODUCTION OPTIMIZED: Generate immediate thumbnail for preview while full compression happens
 * Perfect for iPhone Safari to show instant feedback
 */
export async function generatePreview(
  file: File,
  maxSize: number = 400,
  quality: number = 0.6
): Promise<File> {
  console.log(`🖼️ Generating preview: ${maxSize}px`);
  
  // Skip HEIC conversion for previews - use a fast, low-quality approach
  if (isHeicFile(file)) {
    try {
      // Very fast, low-quality HEIC conversion for preview only
      const blob = await withTimeout(
        heic2any({ blob: file, toType: 'image/jpeg', quality: 0.5 }),
        2000, // Only 2s timeout for preview
        'Preview HEIC conversion',
      );
      const resultBlob = Array.isArray(blob) ? blob[0] : blob;
      file = new File([resultBlob], file.name.replace(/\.[^.]+$/, '.jpg'), {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });
    } catch (err) {
      console.warn('Preview HEIC conversion failed, using original');
      return file; // Return original for preview
    }
  }

  try {
    const bitmap = await createImageBitmap(file);
    const { width, height } = bitmap;
    
    // Calculate thumbnail size maintaining aspect ratio
    let newWidth = width;
    let newHeight = height;
    if (width > maxSize || height > maxSize) {
      const ratio = Math.min(maxSize / width, maxSize / height);
      newWidth = Math.round(width * ratio);
      newHeight = Math.round(height * ratio);
    }

    // Use regular canvas for iPhone Safari compatibility
    const canvas = isiPhoneSafari() && typeof document !== 'undefined'
      ? document.createElement('canvas')
      : new OffscreenCanvas(newWidth, newHeight);
    
    if (canvas instanceof HTMLCanvasElement) {
      canvas.width = newWidth;
      canvas.height = newHeight;
    }
    
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(bitmap, 0, 0, newWidth, newHeight);
    bitmap.close();

    // Fast, low-quality JPEG for preview
    let blob: Blob;
    if (canvas instanceof HTMLCanvasElement) {
      blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((b) => {
          if (b) resolve(b);
          else reject(new Error('Preview canvas toBlob failed'));
        }, 'image/jpeg', quality);
      });
    } else {
      blob = await canvas.convertToBlob({ type: 'image/jpeg', quality });
    }

    const name = file.name.replace(/\.[^.]+$/, '_preview.jpg');
    return new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() });
  } catch (error) {
    console.warn('Preview generation failed, using original:', error);
    return file;
  }
}

/**
 * PRODUCTION OPTIMIZED: Progressive compression for iPhone Safari
 * Returns preview immediately, then full compression
 */
export async function compressWithPreview(
  file: File,
  onPreview?: (preview: File) => void,
  onProgress?: (stage: string) => void,
): Promise<File> {
  onProgress?.('🖼️ Generating preview...');
  
  // Generate and return preview immediately for instant feedback
  const preview = await generatePreview(file);
  onPreview?.(preview);
  
  onProgress?.('🗜️ Compressing full image...');
  
  // Compress full image in background
  const compressed = await compressImage(file);
  
  onProgress?.('✅ Complete');
  return compressed;
}

/**
 * Compress multiple images with optional per-file progress callback.
 * PRODUCTION OPTIMIZED with detailed progress stages for iPhone Safari
 */
export async function compressImages(
  files: File[],
  maxDimension?: number,
  quality?: number,
  onProgress?: (progress: number, stage?: string) => void,
): Promise<File[]> {
  if (files.length === 0) return [];
  
  const results: File[] = [];
  
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const fileProgress = (i / files.length);
    
    onProgress?.(fileProgress, `📷 Processing ${file.name}...`);
    
    try {
      const compressed = await compressImage(file, maxDimension, quality);
      results.push(compressed);
    } catch (error) {
      console.error(`Failed to compress ${file.name}:`, error);
      results.push(file); // Use original if compression fails
    }
    
    const completedProgress = ((i + 1) / files.length);
    onProgress?.(completedProgress, i === files.length - 1 ? '✅ All images ready' : undefined);
  }
  
  return results;
}
